import { useEffect, useState } from "react"
import { useDebounce } from "react-use"
import { Modal, TextInput, Loading } from "@wfp/ui"
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form"
import { UsersData } from "interfaces/dataInterfaces"
import Select from "react-select"
import { regionList, visibilityList } from "constants/constants"
import {
  useAddUserMutation,
  useLazyCheckEmailQuery,
  useLazyGetRolesQuery,
  useUpdateUserMutation,
} from "services/core/user"
import { selectUser } from "features/common/commonSlice"
import { useAppSelector } from "hooks/hooksStore"
import useCommon from "hooks/useCommon"
import { destroyAccessToken } from "utils/token"

interface UserFormProps {
  show: boolean
  item?:
    | (UsersData & {
        mpRoleId?: string
        mfiRoleId?: string
        gorpRoleId?: string
        rpmeRoleId?: string
        householdRoleId?: string
        aimsRoleId?: string
      })
    | null
  closeModal: any
  rolesData: { value: string; label: string }[]
  countriesData: { value: number; label: string }[]
  showSnackBar: any
}

type Inputs = {
  userId: string
  adm0Code: number
  email: string
  firstName: string
  lastName: string
  locationRole: number
  region: number
  modules: string[]
  mpRoleId: string | undefined
  mfiRoleId: string | undefined
  rpmeRoleId: string | undefined
  gorpRoleId: string | undefined
  householdRoleId: string | undefined
  aimsRoleId: string | undefined
}

function UserForm({ show, item = null, closeModal, rolesData = [], countriesData = [], showSnackBar }: UserFormProps) {
  const [addUser, { isLoading: isLoadingAdd }] = useAddUserMutation()
  const [updateUser, { isLoading: isLoadingEdit }] = useUpdateUserMutation()
  const [getRoles] = useLazyGetRolesQuery()
  const [checkEmail] = useLazyCheckEmailQuery()
  const user = useAppSelector(selectUser)
  const { setAuthUser } = useCommon()

  const [emailStatus, setEmailStatus] = useState<string>("notfound")

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    control,
  } = useForm<Inputs>({ mode: "onChange" })

  const locationRole = useWatch({ control, name: "locationRole" })
  const email = useWatch({ control, name: "email" })

  useDebounce(
    async () => {
      if (!item && email && !errors.email) {
        try {
          const result = await checkEmail({ email }).unwrap()
          setEmailStatus(result?.status?.toLowerCase())
        } catch (error) {
          console.error("Error checking email:", error)
          setEmailStatus("notfound")
        }
      }
    },
    400,
    [email, errors.email],
  )

  useEffect(() => {
    reset()
    if (item) {
      setValue("email", item.email)
      setValue("firstName", item.firstName)
      setValue("lastName", item.lastName)
      setValue("mpRoleId", item.mpRoleId)
      setValue("mfiRoleId", item.mfiRoleId)
      setValue("gorpRoleId", item.gorpRoleId)
      setValue("rpmeRoleId", item.rpmeRoleId)
      setValue("householdRoleId", item.householdRoleId)
      setValue("aimsRoleId", item.aimsRoleId)
      setValue("region", item?.region?.regionalBureauId)
      setValue("adm0Code", item.adm0Code)

      if (item.locationRole === "CO") {
        setValue("locationRole", 3)
      }
      if (item.locationRole === "Region") {
        setValue("locationRole", 2)
      }
      if (item.locationRole === "HQ") {
        setValue("locationRole", 1)
      }
    }
  }, [item])

  const onSubmit: SubmitHandler<Inputs> = async data => {
    const value: any = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      mpRoleId: data.mpRoleId || "",
      mfiRoleId: data.mfiRoleId || "",
      gorpRoleId: data.gorpRoleId || "",
      rpmeRoleId: data.rpmeRoleId || "",
      householdRoleId: data.householdRoleId || "",
      aimsRoleId: data.aimsRoleId || "",
      locationRole: data.locationRole,
    }
    if (locationRole === 2) value.region = data.region
    if (locationRole === 3) value.adm0Code = data.adm0Code

    if (item) {
      try {
        await updateUser(value).unwrap()
        if (user?.nameid?.toLowerCase() === value?.userName?.toLowerCase()) {
          destroyAccessToken()
          await setAuthUser(null)
          await getRoles()
        }
        showSnackBar("User updated successfully")
      } catch (error) {
        console.error("Error updating user:", error)
        showSnackBar("User not updated", "error")
      }
    } else {
      try {
        await addUser(value).unwrap()
        showSnackBar("User added successfully")
      } catch (error) {
        console.error("Error adding user:", error)
        showSnackBar("User not added", "error")
      }
    }
    closeModal(false)
    reset()
  }

  const getEmailStatus = (): string => {
    switch (emailStatus) {
      case "active":
        return "This email is already in use."
      case "deleted":
        return "The user is deleted and if you proceed, it will be reactivated."
      default:
        return ""
    }
  }

  return (
    <Modal
      open={show}
      modalHeading={item ? "Modify user" : "Add new user"}
      primaryButtonText="Save"
      primaryButtonDisabled={!isValid || isLoadingAdd || isLoadingEdit}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit(onSubmit)}
      onRequestClose={() => closeModal(false)}
      wide
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {(isLoadingAdd || isLoadingEdit) && <Loading />}
        <div className="border-b-2 border-solid w-100 border-sky-400 pb-1 font-bold">Details</div>
        {emailStatus !== "notfound" && (
          <div className="d-flex mt-2">
            <p className="text-red-600 text-sm">{getEmailStatus()}</p>
          </div>
        )}
        <div className="d-flex mt-2">
          <TextInput
            labelText="E-mail"
            required
            placeholder="E-mail"
            defaultValue={item?.email}
            invalid={errors.email}
            invalidText={errors.email?.message}
            {...register("email", {
              required: "E-mail is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address format",
              },
            })}
            disabled={!!item}
          />
        </div>
        <div className="d-flex mt-3">
          <TextInput
            required
            labelText="First Name"
            placeholder="First Name"
            defaultValue={item?.firstName}
            invalid={errors.firstName}
            invalidText={errors.firstName?.message}
            className="mr-5"
            {...register("firstName", { required: "First name is required" })}
            disabled={emailStatus === "active"}
          />
          <TextInput
            required
            labelText="Last Name"
            placeholder="Last Name"
            defaultValue={item?.lastName}
            invalid={errors.lastName}
            invalidText={errors.lastName?.message}
            {...register("lastName", { required: "Last name is required" })}
            disabled={emailStatus === "active"}
          />
        </div>
        <div className="mt-3 border-b-2 border-solid w-100 border-sky-400 pb-1 font-bold">Roles</div>
        <div className="d-flex mt-2">
          <div className="w-100">
            <label htmlFor="mpRoleId" className="wfp--label">
              Role MP
            </label>
            <Controller
              control={control}
              name="mpRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container mr-5 w-auto"
                  classNamePrefix="wfp--react-select"
                  options={rolesData}
                  ref={ref}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
          <div className="w-100">
            <label htmlFor="mfiRoleId" className="wfp--label">
              Role MFI
            </label>
            <Controller
              control={control}
              name="mfiRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  options={rolesData}
                  ref={ref}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
        </div>
        <div className="d-flex mt-2">
          <div className="w-100">
            <label htmlFor="gorpRoleId" className="wfp--label">
              Role GORP
            </label>
            <Controller
              control={control}
              name="gorpRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container mr-5 w-auto"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={rolesData}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
          <div className="w-100">
            <label htmlFor="rpmeRoleId" className="wfp--label">
              Role RPME
            </label>
            <Controller
              control={control}
              name="rpmeRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container w-auto"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={rolesData}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
        </div>
        <div className="d-flex mt-2">
          <div className="w-100">
            <label htmlFor="householdRoleId" className="wfp--label">
              Role Household
            </label>
            <Controller
              control={control}
              name="householdRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container mr-5 w-auto"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={rolesData}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
          <div className="w-100">
            <label htmlFor="aimsRoleId" className="wfp--label">
              Role AIMS
            </label>
            <Controller
              control={control}
              name="aimsRoleId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container w-auto"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={rolesData}
                  value={value ? rolesData.find(r => r.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value)}
                  placeholder="No Access"
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
        </div>

        <div className="mt-3 border-b-2 border-solid w-100 border-sky-400 pb-1 font-bold">Visibility</div>
        <div className="d-flex mt-2">
          <div className="w-50">
            <label htmlFor="locationRole" className="wfp--label">
              Level*
            </label>
            <Controller
              control={control}
              name="locationRole"
              rules={{ required: "Level is required" }}
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container mr-5 w-auto"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={visibilityList}
                  value={value ? visibilityList.find(v => v.value === value) : null}
                  onChange={itemVal => onChange(itemVal?.value as any)}
                  isDisabled={emailStatus === "active"}
                />
              )}
            />
          </div>
          <div className="w-50">
            {locationRole === 2 && (
              <>
                <label htmlFor="region" className="wfp--label">
                  Region*
                </label>
                <Controller
                  control={control}
                  name="region"
                  rules={{ required: "Region is required" }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Select
                      className="wfp--react-select-container"
                      classNamePrefix="wfp--react-select"
                      ref={ref}
                      options={regionList}
                      value={value ? regionList.find(r => r.value === value) : null}
                      onChange={itemVal => onChange(itemVal?.value as any)}
                      isDisabled={emailStatus === "active"}
                    />
                  )}
                />
                {errors?.region && (
                  <div className="wfp--form-requirement block">
                    <span>Region is required</span>
                  </div>
                )}
              </>
            )}
            {locationRole === 3 && (
              <>
                <label htmlFor="adm0Code" className="wfp--label">
                  Country*
                </label>
                <Controller
                  control={control}
                  name="adm0Code"
                  rules={{ required: "Country is required" }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Select
                      className="wfp--react-select-container"
                      classNamePrefix="wfp--react-select"
                      ref={ref}
                      options={countriesData}
                      value={value ? countriesData.find(r => r.value === value) : null}
                      onChange={itemVal => onChange(itemVal?.value as any)}
                      isDisabled={emailStatus === "active"}
                    />
                  )}
                />
                {errors?.adm0Code && (
                  <div className="wfp--form-requirement block">
                    <span>Country is required</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default UserForm
